import cx from 'clsx';
import React from 'react';

import TableEmptyRowExtended from '@components/common/TableEmptyRowExtended/TableEmptyRowExtended';

import { useDebouncedValue } from '@hooks/useDebouncedValue';
import useTranslation from '@hooks/useTranslation';

import type { ITableSorting } from '@model/tableSorting';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import type { TSourceData } from '../../model/translateSourceDataList';
import { useTranslateSourceDataListQuery } from '../../services/useTranslateSourceDataListQuery';
import {
  selectManageTranslationsSearchValue,
  selectManageTranslationsSelectedLanguage,
} from '../../store/useManageTranslationsStore';
import TranslationsInput from '../TranslationsInput/TranslationsInput';
import TranslationsItemSkeleton from './TranslationsItemSkeleton';
import style from './style.module.scss';

interface IProps {
  type: TSourceData;
  sorting: ITableSorting;
}

const TranslationsItem: React.FC<IProps> = ({ type, sorting }) => {
  const { t } = useTranslation();

  const locationSelectValue = selectAppLocationSelectValue();
  const searchValue = selectManageTranslationsSearchValue();
  const selectedLanguage = selectManageTranslationsSelectedLanguage();

  const [debouncedValue] = useDebouncedValue(searchValue, 500);

  const {
    data: translateSourceDataListData,
    isLoading: translateSourceDataListIsLoading,
    isFetching: translateSourceDataListIsFetching,
  } = useTranslateSourceDataListQuery({
    type,
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
    sorting,
    search: debouncedValue.length >= 3 ? debouncedValue : undefined,
  });

  return (
    <>
      {translateSourceDataListIsLoading ? (
        <TranslationsItemSkeleton />
      ) : translateSourceDataListData?.length ? (
        translateSourceDataListData.map((data) => (
          <tr
            key={data.rowName + '-' + data.name + '-' + data?.translates?.[selectedLanguage?.isoCode]?.id}
            className={cx(style.translateSourceDataRow, {
              [style.rowWithTranslation]: !!selectedLanguage,
            })}
          >
            <td className={style.fieldName}>{data.rowName.replace(/_/g, ' ').toLowerCase()}</td>
            <td className={style.originalName}>{data.name}</td>
            {selectedLanguage && (
              <td>
                <TranslationsInput
                  language={selectedLanguage}
                  translation={data?.translates?.[selectedLanguage.isoCode]}
                  translateSourceData={data}
                  type={type}
                  isLoading={translateSourceDataListIsFetching}
                />
              </td>
            )}
          </tr>
        ))
      ) : (
        <TableEmptyRowExtended text={t('Nothing was found.')} className={style.emptyRow} />
      )}
    </>
  );
};

export default TranslationsItem;
